import type { QueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import type { MessageInstance } from "antd/es/message/interface";
import { closeBill, closeBillRetry } from "../../../../../api/bills";
import type { BillOut } from "../../../../../api/bills";
import {
  billClosingFinished,
  billClosingStarted,
  type AppDispatch,
} from "../../../../../store";
import { getApiErrorMessage } from "../../../../../utils/translations";

/** Изборът от модала „Закрий" — тялото е едно и също за `/close` и `/close/retry`. */
export interface CloseBillSelection {
  billId: number;
  paymentMethodId: number;
  mixedCashAmount?: number;
  mixedCardAmount?: number;
  total?: number;
}

interface BackgroundBillCloseOptions {
  selection: CloseBillSelection;
  /** Закрита без бон сметка — същото тяло, но към `/close/retry`. */
  isRetry: boolean;
  tableName: string;
  snapshot: BillOut | null;
  userId: number | null;
  dispatch: AppDispatch;
  queryClient: QueryClient;
  message: MessageInstance;
  notifyPrinterError: (text: string) => void;
  notifyPrinterTimeout: (text: string) => void;
  t: TFunction;
}

/**
 * Закриването СЛЕД като модалът „Закрий" е затворен: заявката е същата като
 * преди и чака принтера със същите таймаути — само че докато трае, се върти
 * кутийката на масата, а сервитьорът е свободен (docs/ordering.md §Поток:
 * закриване, плащане, фискализация).
 *
 * Не е hook: живее извън компонента, за да довърши и когато сервитьорът е
 * минал в продуктовия изглед (футърът с модала се демонтира) или в друг
 * модул. Затова всичко, което ползва, идва отвън — message/notification
 * инстанциите са на `App` в корена и остават живи.
 *
 * Изходът е като преди, но известието носи масата: успех → toast; timeout и
 * отказ → постоянно известие до ръчно затваряне; 409 → сметката вече е
 * закрита от друг. После кутийката приема каквото върне сървърът — изчезва,
 * или остава „закрита, нефискализирана" според настройката на ресторанта.
 */
export async function runBackgroundBillClose({
  selection,
  isRetry,
  tableName,
  snapshot,
  userId,
  dispatch,
  queryClient,
  message,
  notifyPrinterError,
  notifyPrinterTimeout,
  t,
}: BackgroundBillCloseOptions): Promise<void> {
  const { billId, ...paymentSelection } = selection;
  // Повторното издаване на бон е за сметка, която отдавна е ПЛАТЕНА — отказът
  // там не е „неуспешно плащане" (сервитьорът би взел парите втори път).
  const failureKey = isRetry
    ? "closeTable.tableNotice"
    : "closeTable.paymentFailed";
  dispatch(billClosingStarted({ billId, snapshot, userId }));
  try {
    const result = isRetry
      ? await closeBillRetry(billId, paymentSelection)
      : await closeBill(
          billId,
          paymentSelection.paymentMethodId,
          paymentSelection.mixedCashAmount,
          paymentSelection.mixedCardAmount,
          paymentSelection.total,
        );
    const fiscalResult = result?.fiscal_result;
    if (!fiscalResult || fiscalResult.status === "success") {
      message.success(t("closeTable.closeSuccess"));
    } else if (fiscalResult.status === "timeout") {
      notifyPrinterTimeout(
        t("closeTable.tableNotice", {
          table: tableName,
          text: fiscalResult.error || t("closeTable.printerTimeout"),
        }),
      );
    } else {
      notifyPrinterError(
        t(failureKey, {
          table: tableName,
          text: fiscalResult.error || t("closeTable.closeError"),
        }),
      );
    }
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response
      ?.status;
    // 409 = сметката вече е затворена от паралелна заявка (два таба, две
    // устройства, мрежов retry). Закриването Е минало — оранжево
    // предупреждение, а не червена грешка; бонът — виж „Фискални проблеми".
    if (status === 409) {
      message.warning(
        t("closeTable.tableNotice", {
          table: tableName,
          text: t("closeTable.alreadyClosed"),
        }),
      );
    } else {
      notifyPrinterError(
        t(failureKey, {
          table: tableName,
          text: getApiErrorMessage(error, t("closeTable.closeError")),
        }),
      );
      console.error("Error closing bill:", error);
    }
  } finally {
    // Първо свежият списък, чак после кутийката спира да се върти — иначе за
    // миг би се показала старата (още отворена) сметка от кеша.
    await queryClient
      .invalidateQueries({ queryKey: ["bills"] })
      .catch(() => undefined);
    dispatch(billClosingFinished(billId));
  }
}
